import { createSelector } from '@ngrx/store';
import {
  selectAllPlacements,
  selectAllProperties,
  selectFiles,
} from '@sites/dashboard/store';
import {
  getPreviewCreative,
  isRawMode,
  PreviewCreative,
  PreviewMode,
} from '@sites/dashboard/ui';
import { Creative, Property, Property_ID } from '@sites/data-hmm/hmm-incubator';
import { selectStoreState } from '../store.selectors';
import { CreativeSearch, CreativeSearchViewModel } from './creative.models';
import { creativeAdapter, CreativeKey } from './creative.reducer';

export const selectCreativeState = createSelector(
  selectStoreState,
  (state) => state[CreativeKey]
);

export interface CreativeThumbnailViewModel {
  creative?: PreviewCreative;
}

// Silent and audio modes share file ids with `standard` — the audio
// treatment happens at render time, not in the file selection.
export const recordingFileIdsForMode = (
  creative: Creative,
  previewMode: PreviewMode
): { recording: string; thumbnail: string; gif: string; preview: string } => {
  const isRaw = isRawMode(previewMode);
  return {
    recording: isRaw ? creative.recordingRaw : creative.recording,
    thumbnail: isRaw ? creative.recordingRawThumbnail : creative.thumbnail,
    gif: isRaw ? creative.recordingRawGif : creative.gif,
    // Raw mode never falls back to the normal MP4: without a raw preview it
    // plays the raw GIF, which has no platform frame either.
    preview: isRaw ? creative.recordingRawPreview : creative.recordingPreview,
  };
};

export const selectCreativeThumbnailUrl = (
  creativeId: string,
  previewMode: PreviewMode = 'standard'
) =>
  createSelector(
    selectCreativeById(creativeId),
    selectFiles,
    (creative, files): CreativeThumbnailViewModel => {
      if (!creative) {
        return {
          creative: undefined,
        };
      }
      const fileIds = recordingFileIdsForMode(creative, previewMode);
      return {
        creative: {
          ...getPreviewCreative(
            creative,
            files[fileIds.thumbnail]?.url,
            files[fileIds.gif]?.url,
            files[fileIds.recording]?.url,
            files[fileIds.preview]?.url ?? ''
          ),
        },
      };
    }
  );

export const selectCreativeById = (creativeId: string) =>
  createSelector(selectCreativeState, (state) => {
    return state.entities[creativeId];
  });

export const selectCreativesByIds = (creativeIds: string[]) =>
  createSelector(selectCreativeState, (state) => {
    return creativeIds.map((id) => state.entities[id]);
  });

export const selectCreativesWithResourcesByIds = (creativeIds: string[]) =>
  createSelector(selectCreativeState, (state) => {
    return creativeIds.map((id) => state.entities[id]);
  });

export const selectCreativeSearchViewModel = (search?: CreativeSearch) =>
  createSelector(
    selectAllCreatives,
    selectCreativeState,
    selectAllPlacements,
    selectAllProperties,
    (
      creatives,
      { loading, error },
      placements,
      properties
    ): CreativeSearchViewModel => {
      const propertyMap = properties.reduce(
        (acc, property) => {
          acc[property.id] = property;
          return acc;
        },
        {} as { [key in Property_ID]: Property }
      );

      if (!search) {
        return {
          creatives: [],
          loading,
          error,
          properties,
          propertyMap,
        };
      }

      const filteredCreatives: Creative[] = [];

      creatives.forEach((creative) => {
        if (
          search.studyIds.length > 0 &&
          !search.studyIds.includes(creative.studyId)
        ) {
          return;
        }
        if (
          search.creativeIds.length > 0 &&
          !search.creativeIds.includes(creative.id)
        ) {
          return;
        }
        if (
          search.mediaTypes.length > 0 &&
          !search.mediaTypes.includes(creative.mediaType)
        ) {
          return;
        }

        if (search.propertyId) {
          const matching = placements
            .filter((p) => creative.placementIds.includes(p.id))
            .some((p) => p.propertyId === search.propertyId);
          if (!matching) {
            return;
          }
        }
        if (
          search.placementId &&
          creative.placementIds.includes(search.placementId)
        ) {
          return;
        }
        filteredCreatives.push(creative);
      });

      return {
        creatives: filteredCreatives,
        loading,
        error,
        properties,
        propertyMap,
      };
    }
  );

export const selectCreativesLoading = createSelector(
  selectCreativeState,
  ({ loading }) => loading
);

export const {
  selectAll: selectAllCreatives,
  selectEntities: selectCreativeEntities,
} = creativeAdapter.getSelectors(selectCreativeState);
